import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { MatRadioChange, MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { AdminUIHelpers } from 'src/app/common/helpers';
import { IDisableItemsDto } from 'src/app/common/models/disableItemsDto';
import { FacilityItemInformation } from 'src/app/common/models/facilityItemInformation';
import { IMultiStoreSelect } from 'src/app/common/models/multiStoreSelect';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { ItemsService } from 'src/app/services/items.service';
import { LoggingService } from 'src/app/services/logging.service';
import { StatusService } from 'src/app/services/status.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatStepper, MatStep, MatStepLabel, MatStepperPrevious, MatStepperNext } from '@angular/material/stepper';

import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MultiStoreDropdown } from '../../multi-store-dropdown/multi-store-dropdown.component';

@Component({
  templateUrl: './modifier-availability.component.html',
  styleUrl: './modifier-availability.component.scss',
  standalone: true,
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatStepper,
    MatStep,
    FormsModule,
    ReactiveFormsModule,
    MatStepLabel,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatFormField,
    MatLabel,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
    MatButton,
    MatStepperPrevious,
    MatStepperNext,
    MultiStoreDropdown,
  ],
})
export class ModifierAvailabilityComponent implements OnInit {
  public durationFormGroup: UntypedFormGroup;
  public facilitiesFormGroup: UntypedFormGroup;
  public isUntilFurtherNotice = false;
  public facilities: IMultiStoreSelect[] = [];
  public reason = '';
  public isClickable = true;
  public isMultiStore = false;
  public authManager: AuthManager;
  public currentFacilityId: string;
  public additionalItems: string[] = [];
  public isModifier = false;

  private disableDuration = '1';
  // we want to enforce validation on the duration form group when determining if the button is clickable
  public get isDurationClickable(): boolean {
    return (
      this.isClickable
      // button is clickable if the form is valid or untouched
      && (this.durationFormGroup.valid || !this.durationFormGroup.touched)
    );
  }

  constructor(
    public dialogRef: MatDialogRef<ModifierAvailabilityComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FacilityItemInformation,
    private toastr: ToastrService,
    private authorizationServiceDI: AuthManager,
    public _formBuilder: UntypedFormBuilder,
    private _statusService: StatusService,
    private _facilityManager: FacilityManager,
    private _itemService: ItemsService,
    private logger: LoggingService,
  ) {
    this.currentFacilityId = this._facilityManager.getCurrentFacility();
    this.durationFormGroup = this._formBuilder.group({
      duration: ['1', Validators.required],
      reason: ['', Validators.required],
    });

    this.facilitiesFormGroup = this._formBuilder.group({
      facilities: ['', Validators.required],
    });

    this.durationFormGroup.statusChanges.subscribe((result) =>
      result === 'VALID' ? (this.isClickable = true) : false,
    );

    this.authManager = authorizationServiceDI;
  }

  public ngOnInit(): void {
    this.durationFormGroup.get('duration')?.valueChanges.subscribe((v) => {
      if (v === '0') {
        this.isUntilFurtherNotice = true;
      } else {
        this.isMultiStore = false;
        this.isUntilFurtherNotice = false;
      }
      this.disableDuration = v;
    });
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public multiStoreButton(event: MatRadioChange): void {
    this.isMultiStore = event.value;
  }

  public markDurationFormTouched(): void {
    this.durationFormGroup.markAsTouched();
  }

  public updateAvailability(): void {
    if (!this.durationFormGroup.valid) {
      // this is the 'untouched form' case
      // mark the form as touched, return and don't do anything
      this.markDurationFormTouched();
      return;
    }

    this.isClickable = false;
    const selectedItems: string[] = [this.data.commerceItemNumber];
    let selectedFacilities: string[] = [];

    this.additionalItems.forEach((element) => {
      selectedItems.push(element);
    });

    if (this.isMultiStore) {
      selectedFacilities = AdminUIHelpers.getSelectedFacilityIds(this.currentFacilityId, this.facilities);
    } else {
      selectedFacilities.push(this.currentFacilityId);
    }

    if (!this.data.isAvailable) {
      const itemsToDisable: IDisableItemsDto[] = [];

      let enableDate: string;
      if (this.disableDuration === '0') {
        const tempDateMax = new Date(9999, 11, 31, 23, 59, 59, 999);
        enableDate = moment(tempDateMax).startOf('day').format();
      }
      if (this.disableDuration === '1') {
        enableDate = moment().startOf('day').add(1, 'days').format();
      }
      if (this.disableDuration === '2') {
        enableDate = moment().startOf('day').add(2, 'days').format();
      }

      selectedFacilities.forEach((sf) => {
        selectedItems.forEach((si) => {
          itemsToDisable.push({
            commerceItemNumber: si,
            facilityId: sf,
            organizationId: 1,
            enableOn: enableDate,
          });
        });
      });

      this._itemService.setDisabledItems(itemsToDisable).subscribe({
        next: (r) => {
          this.toastr.success('Item ' + this.data.itemName + ' disabled!');
          this.logger.logEvent('Disabled Items until ' + enableDate.toString() + ': ' + this.data.commerceItemNumber, {
            items: itemsToDisable,
            selectedFacilities: selectedFacilities,
            selectedItems: [this.data.commerceItemNumber],
            currentFacility: this.currentFacilityId,
          });
          this.dialogRef.close(this.data);
        },
        error: (e) => {
          this.toastr.error('Error while disabling Item: ' + e);
          this.logger.logException(e);
          this.logger.logTrace('Error while disabling items', { items: itemsToDisable });
          this.isClickable = true;
        },
      });
    } else if (this.data.isAvailable) {
      const enableObservables = this.buildEnabledObservableCollection(selectedItems, selectedFacilities);
      this._statusService.batchProgressSetup(enableObservables.length);
      forkJoin(enableObservables).subscribe({
        next: (r) => {
          const success = this.observableCollectionLogic(r, this.data.isAvailable);

          if (success) {
            this.dialogRef.close(this.data);
          }
        },
        error: (e) => {
          this.toastr.error('An error occurred while updating Item Availability for Facility ');
          this.isClickable = true;
          return;
        },
      });
    }
  }

  public facilityValidation(): void {
    const facilitiesChecked = this.facilities.filter((f) => f.isSelected).length;
    if (facilitiesChecked) {
      this.facilitiesFormGroup.get('facilities')?.setValue(facilitiesChecked);
      this.isClickable = true;
    } else {
      this.facilitiesFormGroup.get('facilities')?.setValue(null);
      this.isClickable = false;
    }
  }

  public isAvailable(item: FacilityItemInformation): boolean {
    return !item.isAvailable;
  }

  private buildEnabledObservableCollection(items: string[], facilities: string[]): Observable<any>[] {
    const saveObservables: Observable<any>[] = [];
    facilities.forEach((f) => {
      items.forEach((i) => {
        saveObservables.push(
          this._itemService
            .setEnabledItems(1, f, i)
            .pipe(
              map((success) => ({ isError: false, error: null, facilityId: f, item: i })),
              catchError((err) => {
                const errObj = of({ isError: true, error: err, facilityId: f, item: i });
                this.logger.logTrace('enable items error', errObj);
                return errObj;
              }),
            )
            .pipe(
              tap(() => {
                this._statusService.incrementCompletedRequests();
              }),
            ),
        );
      });
    });
    return saveObservables;
  }

  observableCollectionLogic(observableResponses: any[], isAvailable: boolean) {
    const successfulObservables = observableResponses.filter((e) => e.isError === false);
    const erroredObservables = observableResponses.filter((e) => e.isError === true);
    const availabilityStatus = isAvailable ? 'Enabled' : 'Disabled';
    if (erroredObservables.length === 0) {
      this.toastr.success('Item(s) ' + availabilityStatus);
    }
    if (erroredObservables.length > 0 && successfulObservables.length > 0) {
      this.toastr.warning(
        successfulObservables.length
          + ' facility modifiers were '
          + availabilityStatus
          + ', however '
          + erroredObservables.length
          + ' failed to update: '
          + erroredObservables.map((eo) => eo.commerceItemNumber + ' at' + eo.facilityId).join(', '),
        'Not all Facility Modifiers ' + availabilityStatus,
        { timeOut: 10000 },
      );
    }
    if (erroredObservables.length === observableResponses.length) {
      this.toastr.error('An error occurred while updating Facility Modifiers');
      return false;
    }
    return true;
  }
}
